import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import parse from 'html-react-parser';

// Sample data for the FAQ section (answers as HTML <p> blocks)
const faqs = [
  {
    question: "What is a louvered roof pergola?",
    answer: `<p>A louvered roof pergola uses adjustable aluminium slats (louvers) to control light and shelter. Open them for airflow and sun; close them for shade and rain protection. Some models also add smart controls and optional side screens/heating/lighting for year-round use.</p>`,
  },
  {
    question: "Are the pergolas fully waterproof?",
    answer: `<p>Yes — when the louvers are fully closed they form a weatherproof roof. Water is channelled through built-in drainage (e.g., integrated rainwater pipes concealed inside the posts on Classic Pod) to keep the area beneath dry.</p>`,
  },
  {
    question: "Can the pergolas withstand strong winds?",
    answer: `<p>They’re engineered to be robust. Classic Lite is noted as having a weatherproof roof durable enough for extreme winds, and the rest of the range is designed for high wind loads. Exact ratings vary by model and size — ask us for the specific figures for your configuration.</p>`,
  },
  {
    question: "Can I add heating and lighting?",
    answer: `<p>Yes. All models can be specified with integrated ambient LED lighting and instant infra-red heating, so you can use the space comfortably day or night.</p>`,
  },

  // — Additional FAQs from the supplied Word documents —
  {
    question: "What sizes are available?",
    answer: `
      <p><strong>Classic Lite (set sizes):</strong></p>
      <p>• Small — L 3.64m × W 3.50m × H 2.50m</p>
      <p>• Medium — L 4.60m × W 3.50m × H 2.50m</p>
      <p>• Large — L 5.56m × W 3.50m × H 2.50m</p>
      <p><strong>Classic Pod:</strong> Length 2.38–5.95m, Width 1.50–4.50m, Height up to 3.0m</p>
      <p><strong>Deluxe Pod:</strong> Length 2.50–7.00m, Width 1.50–4.50m, Height up to 3.0m</p>
      <p><strong>Deluxe + Pod:</strong> Length 2.50–6.00m, Width 3.00–6.00m, Height up to 3.0m</p>
      <p><strong>Prestige Pod:</strong> Length 2.59–6.00m, Width 1.81–4.00m, Height up to 3.0m</p>
    `,
  },
  {
    question: "What colours and finishes can I choose?",
    answer: `
      <p>• Classic Lite / Classic Pod: Anthracite Grey (RAL 7016) as standard; Classic Pod also offers additional RAL options.</p>
      <p>• Deluxe Pod / Deluxe + Pod / Prestige Pod: Hardware available in any RAL colour.</p>
      <p>• Wood-effect louvred roof is available on Deluxe and Deluxe +.</p>
    `,
  },
  {
    question: "How do I control the roof louvers?",
    answer: `
      <p>All models support remote control and App control.</p>
      <p>• Classic Lite: Streamlined louvers rotate up to 150°.</p>
      <p>• Deluxe +: Wide 210mm double-walled aluminium louvers rotate up to 150°.</p>
      <p>• Prestige: Louvers rotate 90° and the roof can fully retract.</p>
    `,
  },
  {
    question: "Is there a retractable roof option?",
    answer: `<p>Yes — the Prestige Pod has a fully retractable louvred roof that stacks to roughly 13% of the total roof area, giving a true open-air feel when retracted and full weather protection when closed.</p>`,
  },
  {
    question: "Can I add side screens or panels?",
    answer: `<p>Yes — water and fade-resistant fabric side screens are available across the range. On selected models (e.g., Deluxe, Deluxe +, Prestige) you can also specify louvred sliding panels.</p>`,
  },
  {
    question: "How does the drainage work?",
    answer: `<p>Water runs off the closed louvres into hidden channels and is routed down through the posts. For example, Classic Pod has integrated rainwater pipes concealed in the posts. This keeps the perimeter clean and the area beneath dry.</p>`,
  },
  {
    question: "Can these be freestanding or attached to a building?",
    answer: `<p>The Deluxe Pod is highlighted as highly versatile for freestanding or lean-to setups across patios of many sizes. Other models are typically freestanding; suitability for wall-mounting depends on your site and the specific model.</p>`,
  },
];



export default function FaqSection() {
    // State to track the vertical translation of the inner box
    const [translateY, setTranslateY] = useState(50);
    // Ref to attach to the component's container element
    const sectionRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            if (!sectionRef.current) return;

            const rect = sectionRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            // Don't calculate if the element is not in view
            if (rect.bottom < 0 || rect.top > viewportHeight) {
                return;
            }

            // Calculate a value based on the element's position in the viewport.
            // This maps the element's top position (from viewportHeight to 0) to a translateY value (from 50% to 0%).
            // This creates a parallax/reveal effect.
            const newTranslateY = Math.max(0, Math.min(50, (rect.top / viewportHeight) * 50));
            setTranslateY(newTranslateY);
        };

        // Add scroll listener and run once on mount to set initial position
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        // Cleanup scroll listener on component unmount
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        // This outer box acts as a container for the animation.
        // `overflow: 'hidden'` is crucial to hide the inner box before it slides up.
        <Box
            ref={sectionRef}
            sx={{
                height: {md: '100vh', xs: '100%'},
                overflow: 'hidden',
                position: 'relative', // Needed for positioning the inner box,
                backgroundColor: 'rgb(22, 161, 216)'
            }}
        >
            {/* This inner box is the one that will animate */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    backgroundColor: '#1a1a1a',
                    // Apply the transform directly from the state, tied to scroll position
                    transform: `translateY(${translateY}%)`,
                    color: 'white',
                    p: 4, // Added padding for content spacing
                }}
            >
                <Typography variant="h2" component="h2" fontWeight="bold" sx={{ mb: 4 }}>
                    Frequently Asked Questions
                </Typography>

                <Box sx={{ width: '100%', maxWidth: '800px' }}>
                    {faqs.map((faq, index) => (
                        <Accordion
                            key={index}
                            sx={{
                                backgroundColor: '#2c2c2c', // Darker background for accordion
                                color: 'white',
                                mb: 1, // Margin bottom for spacing between items
                                '&:before': {
                                    display: 'none', // Remove default top border
                                },
                                '&.Mui-expanded': {
                                     margin: '8px 0', // Control margin when expanded
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                                aria-controls={`panel${index}a-content`}
                                id={`panel${index}a-header`}
                            >
                                <Typography fontWeight="bold">{parse(faq.question)}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>{parse(faq.answer)}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

